import React, { useEffect, useMemo, useState } from "react";
import styles from "./Banner.module.scss";
import classNames from "classnames";
import dayjs from "dayjs";
import coins from "../../Area/TablesList/assets/coins.png";
import { TournamentPlayerPerTeam } from "../../../graphql/generated";
import players from "../../Area/TablesList/assets/players.png";
import timeIcon from "../../Area/TablesList/assets/time.png";
import { ReactComponent as CalendarIcon } from "../../Area/TablesList/assets/calendar.svg";
import { ReactComponent as ShieldIcon } from "../../Area/TablesList/assets/shield.svg";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useMutation } from "../../../hooks/useMutation";
import {
  PurchaseScheduledTournamentEntryMutation,
  PurchaseScheduledTournamentEntryMutationVariables,
} from "../../Area/TablesList/graphql/JoinScheduledTournamentMutation.generated";
import { loader } from "graphql.macro";
import mpirimpaBanner from "../assets/banners/banner_1.png";
import backgammonClassicBanner from "../assets/banners/backgammonClassicBg.png";
import backgammonPortesBanner from "../assets/banners/backgammonPortesBg.png";
import beloteBanner from "../assets/banners/beloteBg.png";
import coincheBanner from "../assets/banners/coincheBg.png";
import tichuBanner from "../assets/banners/tichuBg.png";
import independentBanner from "../assets/banners/banner_4.png";
import allBanner from "../assets/banners/all_tournaments.png";
import { ScheduledBanner } from "../types/ScheduledBanner";

const JOIN_SCHEDULED_TOURNAMENT_QUERY = loader(
  "../../Area/TablesList/graphql/JoinScheduledTournamentMutation.graphql"
);

const gameBackgrounds: Record<string, string> = {
  mpirimpa_el: mpirimpaBanner,
  mpirimpa_cy: mpirimpaBanner,
  tichu: tichuBanner,
  belote: beloteBanner,
  coinche: coincheBanner,
  backgammon_classic: backgammonClassicBanner,
  backgammon_portes: backgammonPortesBanner,
};

const Banner = ({
  tournamentData,
  showErrorModal,
}: {
  tournamentData: ScheduledBanner;
  showErrorModal: (error: string) => void;
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const [timeLeft, setTimeLeft] = useState<string>("");

  const [, joinScheduleTournament] = useMutation<
    PurchaseScheduledTournamentEntryMutation,
    PurchaseScheduledTournamentEntryMutationVariables
  >(JOIN_SCHEDULED_TOURNAMENT_QUERY, false);

  const bannerBG = useMemo(() => {
    return gameBackgrounds[tournamentData?.gameId];
  }, [tournamentData]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs();
      const start = dayjs(tournamentData?.startDateTime);
      const diffSeconds = start.diff(now, "second");

      if (diffSeconds <= 0) {
        setTimeLeft("");
        clearInterval(interval);
        return;
      }

      if (diffSeconds < 3600) {
        const minutes = Math.floor(diffSeconds / 60);
        setTimeLeft(`${minutes} ${t("instant_tournament.minutes")}`);
      } else {
        setTimeLeft("");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [tournamentData]);

  const getMaxValue = () => {
    const awards = tournamentData?.awards || [];
    const maxValue = Math.max(...awards!.map((a) => a.amount.value));

    return maxValue;
  };

  const handleJoinTournament = () => {
    joinScheduleTournament({
      input: {
        scheduledTournamentId: tournamentData.id,
        invitationId: null,
      },
    }).then((result) => {
      if (result.error) {
        showErrorModal(result.error?.message);
        return result.error;
      } else {
        push(
          `/area/${tournamentData?.roomId}:${tournamentData.gameId}/scheduled-tournaments/${tournamentData.id}`
        );
      }
    });
  };

  const handleContinue = () => {
    push(
      `/area/${tournamentData?.roomId}:${tournamentData.gameId}/scheduled-tournaments/${tournamentData.id}`
    );
  };

  return (
    <div
      className={classNames(
        "flex",
        "gap-6",
        "px-10",
        "text-white",
        "items-center",
        "rounded-lg",
        styles.tournamentItem
      )}
      style={{
        height: "7.5rem",
        backgroundImage: `url(${bannerBG})`,
        backgroundSize: "cover",
      }}
    >
      <div className="flex flex-col gap-1 flex-grow">
        <div className={styles.scheduledTime}>
          <div
            className="flex justify-center items-center"
            style={{
              width: "1.375rem",
              height: "1.375rem",
              borderRadius: "100%",
              background: "#DD6B20",
            }}
          >
            <CalendarIcon width={14} height={14} color="currentColor" />
          </div>
          <div>
            {tournamentData?.startDateTime &&
              dayjs(tournamentData?.startDateTime).format("ddd DD MMM · HH.mm")}
          </div>
        </div>
        <span className="font-semibold text-lg">{tournamentData?.name}</span>
        <div className="flex gap-3 text-sm" style={{ color: "#cccccc" }}>
          <div className="flex items-center gap-2">
            <img src={coins} width={16} height={16} alt="coins" />
            <span>{t("prize")}</span>
            <span className="font-semibold">{getMaxValue()}</span>
          </div>
          <div className="flex items-center gap-2">
            <img src={coins} width={16} height={16} alt="coins" />
            <span>{t("instant_tournament.entry")}</span>
            <span>{tournamentData?.price?.value}</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldIcon width={14} height={14} />
            <span>
              {tournamentData?.playerPerTeam ===
              TournamentPlayerPerTeam.OneVsOne
                ? "1x1"
                : "2x2"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <img src={players} width={16} height={16} alt="coins" />
            <span>{`${t("players")} ${tournamentData?.joinedPlayers}/${
              tournamentData?.numberOfPlayers
            }`}</span>
          </div>
        </div>
      </div>
      <div className="w-2/12 flex flex-col gap-2">
        {!!timeLeft && (
          <div className="flex items-center gap-2">
            <img src={timeIcon} width={26} height={26} alt="" />
            <span className={classNames("text-sm", styles.info)}>
              {t("instant_tournament.starts_in")}
            </span>
            <span className="text-sm font-bold" style={{ color: "#FFAB28" }}>
              {timeLeft}
            </span>
          </div>
        )}
        <button
          className={classNames(
            "btn-white rounded-md shadow-xl text-black w-full py-1 text-sm h-7",
            styles.joinButton
          )}
          onClick={
            tournamentData?.isParticipant
              ? handleContinue
              : handleJoinTournament
          }
        >
          {tournamentData?.isParticipant
            ? t("instant_tournament.continue")
            : t("instant_tournament.join_now")}
        </button>
        <button
          className={classNames(
            "rounded-md shadow-xl w-full text-white py-1 text-sm h-7",
            styles.moreTournamentsButton
          )}
          onClick={() => {
            push(
              `/area/${tournamentData?.roomId}:${tournamentData?.gameId}/scheduled-tournaments`
            );
          }}
        >
          {t("instant_tournament.more_tournaments")}
        </button>
      </div>
    </div>
  );
};

export default Banner;
